/*
 * MeetingRecorder — canonical browser-side audio-capture surface for
 * every note/briefing kind. Iter 91 adds a second recording MODE for
 * capturing meeting audio directly from a browser tab (Zoom / Google
 * Meet / Microsoft Teams web clients) mixed with the advisor's
 * microphone. Historic mic-only mode is preserved as the default so
 * every existing call-site behaves identically.
 *
 * Meeting mode contract (per partner brief):
 *   · Uses navigator.mediaDevices.getDisplayMedia({audio:true, video:true})
 *     — the browser MUST show a source picker; user selects the Zoom /
 *     Teams / Meet tab and MUST toggle "Share audio" for us to receive
 *     an audio track. If the user forgets, we detect the missing track
 *     and stop gracefully rather than silently record nothing.
 *   · The video track granted by getDisplayMedia is discarded immediately
 *     — we only need audio.
 *   · The advisor's microphone is captured in parallel via
 *     getUserMedia({audio:true}) and mixed into the meeting stream
 *     via a shared AudioContext so both voices land in one recording.
 *   · Honest indicators: the UI reports which tracks were actually
 *     granted (Meeting audio · Microphone) — we never pretend to record
 *     what the browser refused to hand us.
 *
 * Progressive capability detection: browser + OS variance around system
 * audio is real (Chrome/Edge on desktop tab-shares deliver it, most
 * others don't). We surface exactly what got granted, no more.
 */
import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Microphone,
  MonitorPlay,
  StopCircle,
  Pause,
  Play,
  Waveform,
  Upload,
  Info,
} from "@phosphor-icons/react";

const MODES = {
  mic: { label: "Microphone", icon: Microphone, hint: "Room, in-person, dictation" },
  meeting: { label: "Meeting audio", icon: MonitorPlay, hint: "Zoom / Meet / Teams tab" },
};

export default function MeetingRecorder({ noteId, onTranscribed, uploadPath = "audio" }) {
  const [mode, setMode] = useState("mic");
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [recorderReady, setRecorderReady] = useState(true);
  // Iter 91 · Which sources actually got captured. In meeting mode both
  // may be true, only one, or (if browser denied everything) none — in
  // which case we abort start().
  const [captured, setCaptured] = useState({ meeting: false, mic: false });

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  // In meeting mode we juggle up to two source streams (tab + mic) plus
  // one mixed stream that MediaRecorder actually consumes; on stop we
  // must tear ALL of them down or the browser leaves red "recording"
  // chips on the tab bar.
  const sourceStreamsRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const pausedAccumRef = useRef(0);
  const pausedAtRef = useRef(null);

  useEffect(() => () => teardown(), []);

  const teardown = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    sourceStreamsRef.current.forEach((s) => s?.getTracks?.().forEach((t) => t.stop()));
    sourceStreamsRef.current = [];
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
    }
    audioCtxRef.current = null;
  };

  const startTimer = () => {
    startedAtRef.current = Date.now();
    pausedAccumRef.current = 0;
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const paused_ms = pausedAtRef.current ? now - pausedAtRef.current : 0;
      setElapsed(Math.floor((now - startedAtRef.current - pausedAccumRef.current - paused_ms) / 1000));
    }, 500);
  };

  /**
   * Combine one or more MediaStreams' audio tracks into a single
   * recordable stream via Web Audio. When there's only one incoming
   * stream we could hand MediaRecorder the raw stream, but routing
   * everything through the AudioContext keeps the recording path
   * identical for mic-only, tab-only, and mic+tab.
   */
  const mixAudio = (streams) => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;
    const dest = ctx.createMediaStreamDestination();
    streams.forEach((s) => {
      if (s?.getAudioTracks?.().length) {
        const src = ctx.createMediaStreamSource(s);
        src.connect(dest);
      }
    });
    return dest.stream;
  };

  const buildRecorder = (stream) => {
    let mime = "audio/webm;codecs=opus";
    if (!MediaRecorder.isTypeSupported(mime)) mime = "audio/webm";
    if (!MediaRecorder.isTypeSupported(mime)) mime = "";
    return mime
      ? new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 24000 })
      : new MediaRecorder(stream);
  };

  const start = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Recording is not supported in this browser.");
      setRecorderReady(false);
      return;
    }
    try {
      let meetingStream = null;
      let micStream = null;
      let hasMeetingAudio = false;

      if (mode === "meeting") {
        if (!navigator.mediaDevices.getDisplayMedia) {
          setError("This browser cannot capture meeting audio — try Chrome or Edge.");
          return;
        }
        // getDisplayMedia demands a video constraint; audio is a HINT
        // (spec requires a video track to be granted). We drop the
        // video track immediately after selection.
        meetingStream = await navigator.mediaDevices.getDisplayMedia({
          audio: true,
          video: { displaySurface: "browser" },
        });
        // Drop video track — recording is audio-only.
        meetingStream.getVideoTracks().forEach((t) => t.stop());
        hasMeetingAudio = meetingStream.getAudioTracks().length > 0;
        if (!hasMeetingAudio) {
          meetingStream.getTracks().forEach((t) => t.stop());
          setError("No meeting audio track — did you toggle 'Share audio' when picking the tab?");
          toast.error("Meeting audio not granted. Pick the tab again and check 'Share audio'.");
          return;
        }
        sourceStreamsRef.current.push(meetingStream);
        // Mic mix in meeting mode. If the mic prompt is denied we STILL
        // proceed with the meeting-only recording; partner brief said
        // include mic ON/OFF, so denial is acceptable.
        try {
          micStream = await navigator.mediaDevices.getUserMedia({
            audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
          });
          sourceStreamsRef.current.push(micStream);
        } catch {
          micStream = null;
          toast.warning("Recording meeting audio without your microphone.");
        }
      } else {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
        });
        sourceStreamsRef.current.push(micStream);
      }

      const inbound = [meetingStream, micStream].filter(Boolean);
      const mixed = mode === "meeting" ? mixAudio(inbound) : micStream;
      streamRef.current = mixed;

      const mr = buildRecorder(mixed);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;

      // If the user stops sharing the tab from Chrome's built-in bar,
      // gracefully end the recording so the transcript reflects what
      // was actually captured.
      if (meetingStream) {
        meetingStream.getAudioTracks().forEach((t) => {
          t.addEventListener("ended", () => {
            if (mediaRef.current?.state !== "inactive") stop();
          });
        });
      }

      mr.start(1000);
      setCaptured({ meeting: !!meetingStream, mic: !!micStream });
      setRecording(true);
      setPaused(false);
      startTimer();
    } catch (e) {
      teardown();
      const msg = e?.name === "NotAllowedError"
        ? "Permission denied — grant tab/mic access to record."
        : (e?.message || "Could not start recording.");
      setError(msg);
    }
  };

  const pause = () => {
    if (!mediaRef.current || mediaRef.current.state !== "recording") return;
    mediaRef.current.pause();
    pausedAtRef.current = Date.now();
    setPaused(true);
  };
  const resume = () => {
    if (!mediaRef.current || mediaRef.current.state !== "paused") return;
    mediaRef.current.resume();
    if (pausedAtRef.current) pausedAccumRef.current += Date.now() - pausedAtRef.current;
    pausedAtRef.current = null;
    setPaused(false);
  };

  const stop = () => {
    if (!mediaRef.current) return;
    try { mediaRef.current.stop(); } catch (e) { console.warn("MeetingRecorder: stop() threw", e); }
    teardown();
    setRecording(false);
    setPaused(false);
  };

  const handleStop = async () => {
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    chunksRef.current = [];
    await uploadBlob(blob, elapsed);
  };

  const uploadBlob = async (blob, durationSec) => {
    setUploading(true);
    setError("");
    try {
      const form = new FormData();
      const ext = (blob.type || "audio/webm").includes("webm") ? "webm" : (blob.type || "audio/webm").split("/")[1];
      form.append("file", blob, `meeting.${ext}`);
      form.append("duration_seconds", String(durationSec || 0));
      const resp = await fetch(`${API}/notes/${noteId}/${uploadPath}`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${resp.status})`);
      }
      const data = await resp.json();
      const langLabel = data.meeting_transcript_lang || data.addendum?.transcript_lang || "detected language";
      const sizeMB = data.meeting_audio_size_bytes ? (data.meeting_audio_size_bytes / 1024 / 1024).toFixed(1) + "MB" : "";
      toast.success(`Transcribed — ${langLabel}${sizeMB ? `, ${sizeMB}` : ""}`);
      onTranscribed?.(data);
    } catch (e) {
      setError(e?.message || "Upload failed");
      toast.error(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const handleFileUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File larger than 25MB — please split before uploading.");
      return;
    }
    await uploadBlob(f, 0);
  };

  const ModeIcon = MODES[mode].icon;

  return (
    <div className="border border-border p-4 bg-secondary/30" data-testid="meeting-recorder">
      {!recording && !uploading && (
        <div className="flex items-center gap-2 mb-3" role="tablist" aria-label="Recording source">
          {Object.entries(MODES).map(([key, m]) => {
            const Icon = m.icon;
            const active = mode === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setMode(key)}
                data-testid={`meeting-record-mode-${key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[0.6875rem] font-mono uppercase tracking-widest border transition-colors ${
                  active
                    ? "border-brass bg-brass/10 text-brass"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon size={12} weight={active ? "fill" : "regular"} /> {m.label}
              </button>
            );
          })}
          <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground/70 ml-1 hidden sm:inline">
            {MODES[mode].hint}
          </span>
        </div>
      )}

      {mode === "meeting" && !recording && !uploading && (
        <div className="mb-3 flex items-start gap-2 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground/80 border border-brass/30 bg-brass/5 p-2" data-testid="meeting-record-guidance">
          <Info size={14} weight="regular" className="shrink-0 mt-0.5 text-brass" />
          <span className="normal-case tracking-normal font-sans">
            Pick the tab hosting your Zoom / Meet / Teams call, then toggle <em>Share audio</em> in the browser's picker. Your microphone is captured alongside if you allow it.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center border ${recording ? "border-destructive/70 bg-destructive/10 animate-pulse" : "border-border"}`}>
            {recording ? (
              <Waveform size={18} weight="fill" className="text-destructive" />
            ) : (
              <ModeIcon size={18} weight={recorderReady ? "regular" : "duotone"} className="text-primary" />
            )}
          </div>
          <div>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
              {recording ? (paused ? "Paused" : "Recording") : uploading ? "Transcribing…" : "Ready"}
            </div>
            <div className="text-sm font-mono tabular-nums" data-testid="meeting-recorder-timer">{fmtTime(elapsed)}</div>
            {recording && mode === "meeting" && (
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1 flex items-center gap-2" data-testid="meeting-record-indicators">
                <span className={captured.meeting ? "text-emerald-500" : "text-muted-foreground/60"}>
                  ● Meeting {captured.meeting ? "capturing" : "off"}
                </span>
                <span className={captured.mic ? "text-emerald-500" : "text-muted-foreground/60"}>
                  ● Mic {captured.mic ? "capturing" : "off"}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {!recording && !uploading && (
            <>
              <button
                data-testid="meeting-record-start"
                onClick={start}
                className="inline-flex items-center gap-1.5 border border-primary bg-primary text-primary-foreground px-3 py-2 text-xs font-medium hover:bg-primary/90"
              >
                <ModeIcon size={13} weight="bold" /> Record {mode === "meeting" ? "meeting" : ""}
              </button>
              <label className="inline-flex items-center gap-1.5 border border-border text-muted-foreground px-3 py-2 text-xs cursor-pointer hover:text-primary hover:border-primary/60">
                <Upload size={13} weight="bold" /> Upload audio
                <input type="file" accept="audio/*" onChange={handleFileUpload} className="hidden" data-testid="meeting-file-upload" />
              </label>
            </>
          )}
          {recording && !paused && (
            <button data-testid="meeting-record-pause" onClick={pause} className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs hover:border-primary/60 hover:text-primary">
              <Pause size={13} weight="bold" /> Pause
            </button>
          )}
          {recording && paused && (
            <button data-testid="meeting-record-resume" onClick={resume} className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs hover:border-primary/60 hover:text-primary">
              <Play size={13} weight="bold" /> Resume
            </button>
          )}
          {recording && (
            <button data-testid="meeting-record-stop" onClick={stop} className="inline-flex items-center gap-1.5 border border-destructive/60 text-destructive px-3 py-2 text-xs hover:bg-destructive/10">
              <StopCircle size={13} weight="bold" /> Stop & transcribe
            </button>
          )}
        </div>
      </div>
      {error && <div className="text-xs text-destructive mt-3" data-testid="meeting-record-error">{error}</div>}
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground/70 mt-3">
        {uploadPath === "audio-addendum"
          ? "Transcript is kept — audio is discarded after processing."
          : "Audio auto-deletes after 30 days · Transcript is kept forever · 25MB / ~90 min limit"}
      </div>
    </div>
  );
}
